import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Mail,
  AlertCircle,
  X,
  Trash2,
  RefreshCw,
  Paperclip,
  CheckCircle,
  ChevronRight,
  Search,
  Filter,
} from "lucide-react";
import axios from "axios";
import { getToken, base_url, email_server_url } from "../../config/config";
import EmailKanbanButton from "../Aufgabe/KanbanComponents/EmailKanbanButton";

// Alert Component with improved design
const Alert = ({ children, variant = "default" }) => {
  const bgColor = variant === "destructive" ? "bg-red-50" : "bg-blue-50";
  const textColor =
    variant === "destructive" ? "text-red-700" : "text-blue-700";
  const borderColor =
    variant === "destructive" ? "border-red-200" : "border-blue-200";

  return (
    <div
      className={`${bgColor} ${textColor} p-4 rounded-lg border ${borderColor} shadow-sm`}
    >
      <div className="flex items-center gap-2">
        <AlertCircle className="h-5 w-5" />
        <span className="font-medium">{children}</span>
      </div>
    </div>
  );
};

// Modal Component with improved design
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm transition-all duration-200">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[150vh] overflow-hidden relative shadow-xl transform transition-all duration-300 scale-100 opacity-100 flex flex-col">
        <button
          onClick={onClose}
          className="absolute right-6 top-6 text-gray-400 hover:text-gray-700 transition-colors z-40"
        >
          <X className="h-6 w-6" />
        </button>
        <div className="p-8 pt-8 overflow-hidden flex flex-col flex-1">
          {children}
        </div>
      </div>
    </div>
  );
};

// BestellungModal Component with improved design
const BestellungModal = ({ isOpen, onClose, email, onSuccess }) => {
  const [categories, setCategories] = useState([]);
  const [bestellungCategoryId, setBestellungCategoryId] = useState(null);
  const [anr, setAnr] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Project selection states
  const [projects, setProjects] = useState([]);
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const [projectsTotal, setProjectsTotal] = useState(0);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsLoadingMore, setProjectsLoadingMore] = useState(false);
  const [projectPage, setProjectPage] = useState(1);

  const projectsPerPage = 10;
  const scrollContainerRef = React.useRef(null);

  // Fetch categories and find "Bestellung" under "Email"
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${base_url}/v1/getAllSubCategories`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await response.json();
        setCategories(data.data);

        // Find the "Bestellung" category under "Email" label
        const bestellungCategory = data.data.find(
          (category) =>
            category.name === "Bestellung" &&
            category.labelId?.name === "Email",
        );

        if (bestellungCategory) {
          setBestellungCategoryId(bestellungCategory._id);
        } else {
          setError("Bestellung-Kategorie konnte nicht gefunden werden");
        }
      } catch (error) {
        setError("Kategorien konnten nicht geladen werden");
        console.error("Fehler beim Laden der Kategorien:", error);
      }
    };
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Fetch projects with infinite scroll
  const fetchProjects = async (page = 1, query = "", isLoadMore = false) => {
    if (isLoadMore) {
      setProjectsLoadingMore(true);
    } else {
      setProjectsLoading(true);
    }
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", projectsPerPage);
      if (query) {
        params.append("search", query);
      }

      const response = await fetch(
        `${base_url}/v1/getAllProjects?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        },
      );
      const data = await response.json();

      // Normalize the response
      const projectsList = data.data || data.projects || data || [];
      const total = data.total || data.length || projectsList.length;

      if (isLoadMore) {
        // Append new projects to existing ones
        setProjects((prev) => [...prev, ...projectsList]);
      } else {
        // Replace with new projects (fresh search)
        setProjects(Array.isArray(projectsList) ? projectsList : []);
      }
      setProjectsTotal(total);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Fehler beim Laden der Projekte");
      if (!isLoadMore) {
        setProjects([]);
      }
    } finally {
      if (isLoadMore) {
        setProjectsLoadingMore(false);
      } else {
        setProjectsLoading(false);
      }
    }
  };

  // Handle project search
  const handleProjectSearch = (query) => {
    setProjectSearchQuery(query);
    setProjectPage(1);
    fetchProjects(1, query);
  };

  // Handle infinite scroll
  const handleScroll = (e) => {
    const container = e.target;
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      50;
    const hasMoreProjects = projects.length < projectsTotal;

    if (isNearBottom && hasMoreProjects && !projectsLoadingMore) {
      setProjectPage((prev) => prev + 1);
      fetchProjects(projectPage + 1, projectSearchQuery, true);
    }
  };

  // Load projects on modal open
  useEffect(() => {
    if (isOpen) {
      setProjectPage(1);
      setProjectSearchQuery("");
      setProjects([]);
      fetchProjects(1, "");
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!anr) {
      setError("Bitte wählen Sie ein Projekt aus");
      return;
    }
    if (!bestellungCategoryId) {
      setError("Kategorie-ID konnte nicht gefunden werden");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${base_url}/v1/addEmailToProject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          categoryId: bestellungCategoryId,
          messageId: email.messageId,
          ANR: anr,
        }),
      });
      if (!response.ok)
        throw new Error("Fehler beim Hinzufügen zur Bestellung");
      onSuccess?.();
      onClose();
    } catch (error) {
      setError("Fehler beim Speichern der Bestellung");
      console.error("Fehler:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header Section */}
        <div className="flex-shrink-0 border-b border-gray-200 pb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Zu Documents hinzufügen
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Bitte füllen Sie die erforderlichen Felder aus
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto py-4 min-h-0">
          {error && <Alert variant="destructive">{error}</Alert>}

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Kategorie
              </label>
              <div className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Email</span>
                  <span className="text-gray-400">›</span>
                  <span>Bestellung</span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                ANR <span className="text-red-500">*</span>
              </label>

              {/* Search input with expandable results */}
              <div className="relative z-50">
                <input
                  type="text"
                  placeholder="ANR suchen..."
                  value={projectSearchQuery}
                  onChange={(e) => handleProjectSearch(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-colors text-sm"
                  disabled={loading}
                />

                {/* Results dropdown that expands down */}
                {projectSearchQuery && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-2xl z-50">
                    {/* Projects list with infinite scroll */}
                    <div
                      ref={scrollContainerRef}
                      onScroll={handleScroll}
                      className="max-h-64 overflow-y-auto"
                    >
                      {projectsLoading ? (
                        <div className="p-3 text-center text-gray-600 text-sm">
                          Laden...
                        </div>
                      ) : projects.length > 0 ? (
                        <>
                          {projects.map((project) => {
                            const projectANR =
                              project.ANR ||
                              project.anr ||
                              project._id ||
                              project.id;
                            const projectName =
                              project.name || project.projectName || projectANR;
                            return (
                              <button
                                key={projectANR}
                                onClick={() => {
                                  setAnr(projectANR);
                                  setProjectSearchQuery("");
                                  setProjects([]);
                                }}
                                className="w-full text-left p-3 hover:bg-blue-50 transition-colors border-b last:border-b-0 text-sm"
                              >
                                <div className="font-medium text-gray-900">
                                  {projectANR}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {projectName}
                                </div>
                              </button>
                            );
                          })}
                          {projectsLoadingMore && (
                            <div className="p-3 text-center text-gray-600 text-sm bg-gray-50">
                              Mehr laden...
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="p-3 text-center text-gray-600 text-sm">
                          Keine Projekte gefunden
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Selected ANR display */}
                {anr && !projectSearchQuery && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      <span className="font-medium">Ausgewählt:</span> {anr}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex-shrink-0 border-t border-gray-200 pt-4 mt-4">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 shadow-sm transition-colors font-medium"
          >
            {loading ? "Wird gespeichert..." : "Bestätigen"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Main EmailInbox Component
const EmailInbox = () => {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [processedEmailIds, setProcessedEmailIds] = useState(new Set());
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isBestellungModalOpen, setIsBestellungModalOpen] = useState(false);
  const [selectedEmailUIDs, setSelectedEmailUIDs] = useState(new Set());

  const eventSourceRef = useRef(null);
  const processedEmails = useRef(new Set());

  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 3000;
  const SERVER_URL = `${email_server_url}/events`;

  // Task status per E-Mail (by emailId/messageId)
  const [emailTaskStatus, setEmailTaskStatus] = useState({});

  // Status color + label maps
  const statusColorMap = {
    assigned: "#f59e0b", // Amber
    "in-progress": "#3b82f6", // Blue
    completed: "#10b981", // Green
    archived: "#6b7280", // Gray
    missing: "#ef4444", // Red for no record
  };

  const statusLabelMap = {
    assigned: "Zugewiesen",
    "in-progress": "In Bearbeitung",
    completed: "Abgeschlossen",
    archived: "Archiviert",
    missing: "Kein Eintrag",
  };

  const getStatusKey = (email) => {
    if (!email) return "missing";
    const key = email.uid || email.messageId;
    return emailTaskStatus[key] || "missing";
  };

  // Fetch task status for visible emails from /getEmailTasks
  const fetchEmailTaskStatus = async (emailIds) => {
    if (!emailIds || emailIds.length === 0) return;

    try {
      const response = await axios.get(`${base_url}/v1/getEmailTasks`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

      const statusMap = {};
      data.forEach((item) => {
        const key = item.emailId || item.messageId;
        if (key) {
          statusMap[key] = item.status;
        }
      });

      setEmailTaskStatus(statusMap);
    } catch (error) {
      console.error("Error fetching email task status:", error);
      setEmailTaskStatus({});
    }
  };

  // Call this function when emails are loaded or filtered
  useEffect(() => {
    if (messages.length > 0) {
      const emailIds = messages
        .map((email) => email.uid || email.messageId)
        .filter(Boolean);
      fetchEmailTaskStatus(emailIds);
    } else {
      setEmailTaskStatus({});
    }
  }, [messages]);
  // Fetch processed email IDs
  useEffect(() => {
    const fetchProcessedEmails = async () => {
      try {
        const response = await fetch(`${base_url}/v1/bestellung/getAllEmails`);
        const data = await response.json();
        const processedIds = new Set(data.map((email) => email.emailId));
        setProcessedEmailIds(processedIds);
      } catch (error) {
        console.error("Fehler beim Laden verarbeiteter E-Mails:", error);
      }
    };
    fetchProcessedEmails();
  }, []);

  const cleanEmailBody = (body) => {
    if (!body) return "";
    const lines = body.split("\n");
    const cleanedLines = lines.filter((line) => !line.trim().startsWith(">"));
    while (cleanedLines.length && !cleanedLines[0].trim()) cleanedLines.shift();
    while (cleanedLines.length && !cleanedLines[cleanedLines.length - 1].trim())
      cleanedLines.pop();
    return cleanedLines.join("\n");
  };

  const cleanupEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const connectToSSE = useCallback(() => {
    try {
      cleanupEventSource();
      const eventSource = new EventSource(SERVER_URL);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
        setError(null);
        setReconnectAttempt(0);
      };

      // Handle new emails from the server
      eventSource.onmessage = (event) => {
        try {
          const newEmail = JSON.parse(event.data);
          if (!processedEmails.current.has(newEmail.messageId)) {
            processedEmails.current.add(newEmail.messageId);
            const cleanedEmail = {
              ...newEmail,
              body: cleanEmailBody(newEmail.body),
            };
            setMessages((prevMessages) => {
              const updatedMessages = [cleanedEmail, ...prevMessages];
              return updatedMessages.sort(
                (a, b) => new Date(b.date) - new Date(a.date),
              );
            });
          }
        } catch (err) {
          console.error("Fehler beim Verarbeiten der Nachricht:", err);
        }
      };

      // Listen for deletion events from the server
      eventSource.addEventListener("delete", (event) => {
        try {
          const { uid } = JSON.parse(event.data);
          setMessages((prev) => prev.filter((email) => email.uid !== uid));
          setFilteredMessages((prev) =>
            prev.filter((email) => email.uid !== uid),
          );
        } catch (err) {
          console.error("Fehler beim Verarbeiten des Delete-Events:", err);
        }
      });

      eventSource.onerror = () => {
        setIsConnected(false);
        cleanupEventSource();
        if (reconnectAttempt < MAX_RECONNECT_ATTEMPTS) {
          setError(
            `Verbindung verloren. Verbindungsversuch (${
              reconnectAttempt + 1
            }/${MAX_RECONNECT_ATTEMPTS})...`,
          );
          setReconnectAttempt((prev) => prev + 1);
          setTimeout(connectToSSE, RECONNECT_DELAY);
        } else {
          setError(
            "Maximale Anzahl an Verbindungsversuchen erreicht. Bitte laden Sie die Seite neu.",
          );
        }
      };
    } catch (err) {
      setError(`Verbindungsfehler: ${err.message}`);
      setIsConnected(false);
    }
  }, [reconnectAttempt, cleanupEventSource]);

  useEffect(() => {
    connectToSSE();
    return cleanupEventSource;
  }, [connectToSSE, cleanupEventSource]);

  useEffect(() => {
    let filtered = messages.filter(
      (message) =>
        message.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        message.from?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        message.body?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((message) => {
        const status = getStatusKey(message);
        return status === statusFilter;
      });
    }

    setFilteredMessages(filtered);
  }, [messages, searchTerm, statusFilter, emailTaskStatus]);

  const refreshInbox = useCallback(() => {
    setReconnectAttempt(0);
    setError(null);
    cleanupEventSource();
    connectToSSE();
  }, [cleanupEventSource, connectToSSE]);

  const formatDate = (dateString) => {
    const messageDate = new Date(dateString);
    const today = new Date();

    // If the message is from today, just show the time
    if (messageDate.toDateString() === today.toDateString()) {
      return messageDate.toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    // If it's from this year but not today, show day and month
    if (messageDate.getFullYear() === today.getFullYear()) {
      return messageDate.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
      });
    }

    // Otherwise show the full date
    return messageDate.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Handler for individual email deletion
  const handleDeleteEmail = async () => {
    if (!selectedEmail || !selectedEmail.uid) return;
    try {
      const response = await fetch(
        `${email_server_url}/emails${selectedEmail.uid}`,
        {
          method: "DELETE",
        },
      );
      if (!response.ok) throw new Error("Fehler beim Löschen der E-Mail");
      setMessages((prevMessages) =>
        prevMessages.filter((m) => m.uid !== selectedEmail.uid),
      );
      setFilteredMessages((prev) =>
        prev.filter((m) => m.uid !== selectedEmail.uid),
      );
      setIsEmailModalOpen(false);
    } catch (error) {
      console.error("Fehler beim Löschen der E-Mail:", error);
    }
  };

  // Handler for bulk deletion
  const handleBulkDelete = async () => {
    if (selectedEmailUIDs.size === 0) return;
    if (
      !window.confirm("Möchten Sie die ausgewählten E-Mails wirklich löschen?")
    )
      return;
    try {
      const uidsToDelete = Array.from(selectedEmailUIDs);
      const response = await fetch(`${email_server_url}/emailsbulk`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uids: uidsToDelete }),
      });
      if (!response.ok) throw new Error("Fehler beim Löschen der E-Mails");
      setMessages((prevMessages) =>
        prevMessages.filter((m) => !selectedEmailUIDs.has(m.uid)),
      );
      setFilteredMessages((prev) =>
        prev.filter((m) => !selectedEmailUIDs.has(m.uid)),
      );
      setSelectedEmailUIDs(new Set());
    } catch (error) {
      console.error("Bulk delete error:", error);
    }
  };

  // Handler for checkbox selection changes
  const toggleEmailSelection = (uid, checked) => {
    setSelectedEmailUIDs((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(uid);
      } else {
        newSet.delete(uid);
      }
      return newSet;
    });
  };

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden font-sans">
      {/* Header */}
      <div className="bg-white border-b shadow-sm h-16 flex items-center px-6 shrink-0 z-10 justify-between">
        <div className="flex items-center">
          <Mail className="h-7 w-7 text-blue-600" />
          <span className="ml-2 text-xl text-gray-800 font-medium">
            E-Mail Posteingang
          </span>
        </div>
        <button
          onClick={refreshInbox}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          title="Aktualisieren"
        >
          <RefreshCw
            className={`h-5 w-5 text-gray-600 ${
              !isConnected ? "animate-spin" : ""
            }`}
          />
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white border-b shadow-sm px-6 py-4 shrink-0">
        <div className="flex gap-4 items-center">
          {/* Search Input */}
          <div className="flex-1 max-w-2xl relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="E-Mails durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <option value="all">Alle Status</option>
              <option value="assigned">Zugewiesen</option>
              <option value="in-progress">In Bearbeitung</option>
              <option value="completed">Abgeschlossen</option>
              <option value="archived">Archiviert</option>
              <option value="missing">Kein Eintrag</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Delete Action Bar */}
      {selectedEmailUIDs.size > 0 && (
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex justify-between items-center">
          <span className="font-medium text-blue-800">
            {selectedEmailUIDs.size} E-Mail(s) ausgewählt
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedEmailUIDs(new Set())}
              className="px-4 py-2 bg-white text-gray-700 rounded hover:bg-gray-100 border border-gray-300 transition-colors"
            >
              Auswahl aufheben
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4 inline-block mr-1.5" />
              Löschen
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-white">
        {error && (
          <div className="p-4 bg-red-50 border-b border-red-100">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Email list header */}
        <div className="sticky top-0 bg-white border-b px-6 py-3 z-10">
          <div className="flex items-center">
            <h2 className="font-medium text-gray-800">E-Mails</h2>
            <span className="ml-2 text-sm text-gray-500">
              ({filteredMessages.length})
            </span>
          </div>
        </div>

        {/* Legende für Aufgabenstatus */}
        <div className="px-4 py-3 border-b bg-white flex flex-wrap items-center gap-3 text-xs text-gray-700">
          <span className="font-semibold text-gray-800">Legende:</span>
          {["assigned", "in-progress", "completed", "archived", "missing"].map(
            (key) => (
              <span
                key={key}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full border"
                style={{
                  backgroundColor: `${statusColorMap[key] || statusColorMap.missing}20`,
                  color: statusColorMap[key] || statusColorMap.missing,
                  borderColor: `${statusColorMap[key] || statusColorMap.missing}33`,
                }}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor:
                      statusColorMap[key] || statusColorMap.missing,
                  }}
                />
                {statusLabelMap[key]}
              </span>
            ),
          )}
        </div>

        {/* Email list */}
        <div className="divide-y">
          {filteredMessages.length === 0 ? (
            <div className="text-center text-gray-500 py-16">
              <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">
                {searchTerm
                  ? "Keine E-Mails gefunden"
                  : "Keine E-Mails vorhanden"}
              </p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Versuchen Sie andere Suchbegriffe"
                  : "Neue Nachrichten erscheinen hier"}
              </p>
            </div>
          ) : (
            filteredMessages.map((message) => (
              <div
                key={message.messageId}
                className={`hover:shadow-md transition-shadow cursor-pointer relative ${
                  processedEmailIds.has(message.messageId)
                    ? "bg-green-50 hover:bg-green-100"
                    : "bg-white hover:bg-gray-50"
                }`}
                onClick={() => {
                  setSelectedEmail(message);
                  setIsEmailModalOpen(true);
                }}
              >
                <div className="flex items-center px-4 py-3 gap-3">
                  {/* Checkbox for bulk selection */}
                  <div className="flex-shrink-0 w-8">
                    <input
                      type="checkbox"
                      checked={selectedEmailUIDs.has(message.uid)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleEmailSelection(message.uid, e.target.checked);
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex justify-between items-baseline">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {message.from}
                      </p>
                      <div className="ml-2 flex-shrink-0 flex items-center gap-2">
                        {(() => {
                          const statusKey = getStatusKey(message);
                          const color =
                            statusColorMap[statusKey] || statusColorMap.missing;
                          const label =
                            statusLabelMap[statusKey] || statusLabelMap.missing;
                          return (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: `${color}20`,
                                color,
                                border: `1px solid ${color}33`,
                              }}
                              title={label}
                            >
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: color }}
                              />
                              {label}
                            </span>
                          );
                        })()}
                        <p className="text-xs text-gray-500">
                          {formatDate(message.date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate flex-1">
                        {message.subject}
                      </p>
                      {message.attachments &&
                        message.attachments.length > 0 && (
                          <span
                            className="flex-shrink-0 flex items-center gap-1 text-gray-500"
                            title={`${message.attachments.length} Anhang/Anhänge`}
                          >
                            <Paperclip className="h-3.5 w-3.5" />
                            <span className="text-xs">
                              {message.attachments.length}
                            </span>
                          </span>
                        )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">
                      {message.body}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEmail(message);
                      setIsEmailModalOpen(true);
                    }}
                    className="flex-shrink-0 px-3 py-2 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Email Detail Modal */}
      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
      >
        {selectedEmail && (
          <div className="flex flex-col h-full overflow-hidden">
            {/* Header Section */}
            <div className="flex-shrink-0 border-b border-gray-200 pb-4">
              <div className="flex justify-between items-start gap-4 mb-4">
                <h2 className="text-2xl font-bold text-gray-900 break-words flex-1">
                  {selectedEmail.subject}
                </h2>
                {processedEmailIds.has(selectedEmail.messageId) && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex-shrink-0 whitespace-nowrap">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Verarbeitet
                  </span>
                )}
              </div>

              {/* Email Metadata */}
              <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                <div className="flex items-start gap-4">
                  <span className="text-sm font-semibold text-gray-700 min-w-16">
                    Von:
                  </span>
                  <span className="text-sm text-gray-900 break-all">
                    {selectedEmail.from}
                  </span>
                </div>
                <div className="flex items-start gap-4">
                  <span className="text-sm font-semibold text-gray-700 min-w-16">
                    Datum:
                  </span>
                  <span className="text-sm text-gray-900">
                    {new Date(selectedEmail.date).toLocaleString("de-DE")}
                  </span>
                </div>
                {selectedEmail.to && (
                  <div className="flex items-start gap-4">
                    <span className="text-sm font-semibold text-gray-700 min-w-16">
                      An:
                    </span>
                    <span className="text-sm text-gray-900 break-all">
                      {selectedEmail.to}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Email Body - Scrollable */}
            <div className="flex-1 overflow-y-auto py-4 min-h-0">
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mb-6">
                <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed font-sans">
                  {selectedEmail.body || (
                    <span className="text-gray-500 italic">
                      Keine Nachricht vorhanden
                    </span>
                  )}
                </div>
              </div>

              {/* Attachments section if available */}
              {selectedEmail.attachments &&
                selectedEmail.attachments.length > 0 && (
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Paperclip className="h-5 w-5 text-gray-600" />
                      Anhänge ({selectedEmail.attachments.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedEmail.attachments.map((attachment, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
                        >
                          <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded flex items-center justify-center">
                            <Paperclip className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 font-medium truncate">
                              {attachment.filename ||
                                attachment.name ||
                                "Unbenannte Datei"}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {attachment.contentType && (
                                <span className="text-xs text-gray-500">
                                  {attachment.contentType}
                                </span>
                              )}
                              {attachment.size && (
                                <span className="text-xs text-gray-500">
                                  •{" "}
                                  {typeof attachment.size === "number"
                                    ? `${(attachment.size / 1024).toFixed(2)} KB`
                                    : attachment.size}
                                </span>
                              )}
                            </div>
                          </div>
                          {attachment.content && (
                            <button
                              onClick={() => {
                                // Download attachment
                                const link = document.createElement("a");
                                link.href = `data:${attachment.contentType || "application/octet-stream"};base64,${attachment.content}`;
                                link.download =
                                  attachment.filename ||
                                  attachment.name ||
                                  "download";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                            >
                              Download
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex-shrink-0 border-t border-gray-200 pt-4 mt-4 flex gap-3 justify-end">
              <button
                onClick={() => handleDeleteEmail()}
                className="px-4 py-2.5 bg-white text-red-600 font-medium rounded-lg hover:bg-red-50 border border-red-300 transition-colors flex items-center gap-2 text-sm"
              >
                <Trash2 className="h-4 w-4" />
                Löschen
              </button>
              <button
                onClick={() => {
                  setIsEmailModalOpen(false);
                  setIsBestellungModalOpen(true);
                }}
                className="px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Zu Bestellung hinzufügen
              </button>
              <EmailKanbanButton
                email={selectedEmail}
                taskCount={getStatusKey(selectedEmail) === "missing" ? 0 : 1}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Bestellung Modal */}
      <BestellungModal
        isOpen={isBestellungModalOpen}
        onClose={() => setIsBestellungModalOpen(false)}
        email={selectedEmail}
        onSuccess={() => {
          setProcessedEmailIds(
            (prev) => new Set([...prev, selectedEmail?.messageId]),
          );
        }}
      />
    </div>
  );
};

export default EmailInbox;
